import { Add, Close, Save } from '@mui/icons-material';
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Checkbox,
  Theme,
  Typography,
  Box,
  ButtonGroup,
  Button,
  Divider
} from '@mui/material';
import { useQuery } from 'react-query';
import { Form, Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC } from 'react';
import { Loader } from '../../../../components';
import { IDropdownResponse, IPlanRooms } from '../../../../models';
import { addPlanRooms, getPlanRoomOptions } from '../../../../fetch';

interface IPlanRoomDetails {
  rooms: number[];
  isLoadingRooms: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchRooms: () => void;
}

const RoomsSchema = Yup.object().shape({
  planId: Yup.number(),
  planRoomIds: Yup.array()
});

export const PlanRooms: FC<IPlanRoomDetails> = ({
  rooms,
  isLoadingRooms,
  setDirtyForm,
  refetchRooms
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const history = useHistory();
  const { planId }: { planId: string } = useParams();

  const {
    isLoading: isLoadingRoomOptions,
    data: roomOptions,
    refetch: refetchRoomOptions
  } = useQuery<IDropdownResponse[], Error>(['getPlanRoomOptions'], () => getPlanRoomOptions());

  return (
    <>
      {isLoadingRooms && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingRooms && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            planRoomIds: rooms ?? []
          }}
          validationSchema={RoomsSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IPlanRooms = {
                ...values
              };
              await addPlanRooms(planId, data);
              refetchRooms();
              refetchRoomOptions();
              enqueueSnackbar('Plan rooms have been updated successfully!', {
                variant: 'success'
              });
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Something went wrong. Plan was not updated.', {
                variant: 'error'
              });
            }
          }}
        >
          {({ isSubmitting, values, setFieldValue, handleSubmit, dirty, isValid }) => {
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }

            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}
                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <FormControl component="fieldset" variant="standard">
                          <Typography className={classes.subtitle}>
                            Please select all rooms that are applicable to this plan:
                          </Typography>
                          <FormGroup className={classes.checkboxGroup}>
                            {!isLoadingRoomOptions &&
                              roomOptions?.map((room, index) => {
                                return (
                                  <FormControlLabel
                                    key={`${index}`}
                                    control={
                                      <Checkbox
                                        checked={values.planRoomIds.includes(Number(room.value))}
                                        onChange={() => {
                                          values.planRoomIds.includes(Number(room.value))
                                            ? setFieldValue(
                                                'planRoomIds',
                                                values.planRoomIds.filter(
                                                  val => val !== Number(room.value)
                                                )
                                              )
                                            : setFieldValue('planRoomIds', [
                                                ...values.planRoomIds,
                                                Number(room.value)
                                              ]);
                                        }}
                                      />
                                    }
                                    label={room.description}
                                  />
                                );
                              })}
                          </FormGroup>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={planId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/plans/manage-plans');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  checkboxGroup: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    '@media (max-width: 1500px)': {
      gridTemplateColumns: 'repeat(3, 1fr)'
    },
    '@media (max-width: 550px)': {
      gridTemplateColumns: 'repeat(2, 1fr)'
    }
  },
  subtitle: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(1)
  }
}));
